/* FALL DEAL widget - FALL5
   Display only: reads variant prices, never touches price, cart or checkout.
   No intervals, no animation libraries; all motion is CSS or one-shot WAAPI. */
(() => {
  if (!window.customElements || customElements.get('fall-deal')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const enteredProducts = new Set(); // entrance plays once per product per page view
  const instances = new Set();

  /* ---------- money: exact amount with cents, e.g. "27,95 €" / "46 €" ---------- */
  function formatEur(cents) {
    const eur = Math.floor(cents / 100);
    const cent = cents % 100;
    return cent === 0 ? eur + ' €' : eur + ',' + String(cent).padStart(2, '0') + ' €';
  }

  /* ---------- savings logic ---------- */
  // 5 % of the price in cents, rounded half up (same integer math as the Liquid snippet)
  function codeSavings(priceCents, percent) {
    return Math.floor((priceCents * percent + 50) / 100);
  }

  function computeDeal(variant, percent, minCents) {
    const price = Number(variant.p) || 0;
    const compare = Number(variant.c) || 0;
    const saleSave = compare > price ? compare - price : 0; // only a valid compare-at counts
    const eligible = price >= minCents; // inclusive: exactly 19,00 € qualifies
    const codeSave = codeSavings(price, percent);
    return {
      eligible,
      price,
      saleSave,
      codeSave,
      final: price - codeSave, // what the customer pays with the code
      total: saleSave + codeSave,
    };
  }

  /* ---------- clipboard ---------- */
  async function copyText(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) { /* fall through to legacy copy */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch (e) {
      return false;
    }
  }

  class FallDeal extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      instances.add(this);

      if (!this._initialized) this.init();

      // entrance: once per product; instant when re-rendered or reduced motion
      const pid = this.dataset.productId;
      if (!this.hasAttribute('data-entered') && (reducedMotion.matches || enteredProducts.has(pid))) {
        this.setAttribute('data-entered', 'instant');
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.setAttribute('data-visible', '');
              if (!this.hasAttribute('data-entered')) {
                this.setAttribute('data-entered', 'anim');
                enteredProducts.add(pid);
                // after it has played once, never let a show/hide restart it
                const done = (ev) => {
                  if (ev.animationName !== 'fd-enter') return;
                  this.setAttribute('data-entered', 'done');
                  this.card.removeEventListener('animationend', done);
                  this.card.removeEventListener('animationcancel', done);
                };
                this.card.addEventListener('animationend', done);
                this.card.addEventListener('animationcancel', done);
              }
            } else {
              this.removeAttribute('data-visible');
            }
          });
        }, { threshold: 0.15 });
        this.observer.observe(this);
      } else {
        this.setAttribute('data-visible', '');
        this.setAttribute('data-entered', 'instant');
      }
      if (this.getAttribute('data-entered') === 'instant') enteredProducts.add(pid);

      this.sync();
    }

    disconnectedCallback() {
      this._connected = false;
      instances.delete(this);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this._copyTimer);
    }

    init() {
      this._initialized = true;
      this.card = this.querySelector('.fd');
      this.amountEl = this.querySelector('[data-fd-amount]');
      this.finalEl = this.querySelector('[data-fd-final]');
      this.baseEl = this.querySelector('[data-fd-base]');
      this.button = this.querySelector('[data-fd-copy]');
      this.live = this.querySelector('[data-fd-live]');
      this.appliedEl = this.querySelector('[data-fd-applied]');
      this.autoApply = this.dataset.autoApply !== 'false';
      try {
        if (this.autoApply && sessionStorage.getItem('fd-applied') === this.dataset.code) this.showApplied();
      } catch (e) { /* storage blocked - fine */ }
      this.code = this.dataset.code || 'FALL5';
      this.percent = Number(this.dataset.percent) || 5;
      this.minCents = Number(this.dataset.minCents) || 1900;
      this.currentId = String(this.dataset.variantId || '');

      this.variants = new Map();
      try {
        JSON.parse(this.dataset.variants || '[]').forEach((v) => this.variants.set(String(v.id), v));
      } catch (e) { /* keep server-rendered values */ }

      if (this.dataset.ends) {
        const lastDay = new Date(this.dataset.ends + 'T23:59:59');
        if (!isNaN(lastDay) && Date.now() > lastDay.getTime()) this.hidden = true;
      }

      if (this.button) this.button.addEventListener('click', () => this.copy());
    }

    /* which variant is selected right now */
    selectedVariantId() {
      const sid = this.dataset.sectionId;
      const scope = (sid && document.getElementById('MainProduct-' + sid)) || this.closest('section') || document;
      const input =
        (sid && document.querySelector('#product-form-' + sid + ' [name="id"]')) ||
        scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return String(input.value);
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      return fromUrl ? String(fromUrl) : this.currentId;
    }

    sync(variantId) {
      const id = String(variantId || this.selectedVariantId() || '');
      if (!id || !this.variants.has(id)) return;
      const changed = id !== this.currentId;
      this.currentId = id;
      this.render(this.variants.get(id), changed);
    }

    render(variant, animate) {
      const deal = computeDeal(variant, this.percent, this.minCents);

      // only sizes from the minimum price up show the box
      if (this.hidden === deal.eligible) this.hidden = !deal.eligible;
      if (!deal.eligible) return;

      const changed = [];
      const set = (el, text) => {
        if (!el || el.textContent.trim() === text) return;
        el.textContent = text;
        changed.push(el);
      };
      set(this.finalEl, formatEur(deal.final));
      set(this.baseEl, formatEur(deal.price));
      set(this.amountEl, formatEur(deal.total));

      // animate only the numbers that actually changed
      if (animate && !reducedMotion.matches) {
        changed.forEach((el) => {
          if (el === this.baseEl || !el.animate) return;
          el.animate(
            [
              { opacity: 0.2, transform: 'translateY(4px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' }
          );
        });
      }
    }

    showApplied() {
      if (this.appliedEl) this.appliedEl.hidden = false;
    }

    // Shopify's discount link stores the code for checkout; fetched quietly, no page change
    async applyToCheckout() {
      if (!this.autoApply) return false;
      try {
        const url = '/discount/' + encodeURIComponent(this.code) + '?redirect=%2Fcart.js';
        const res = await fetch(url, { credentials: 'same-origin', redirect: 'follow' });
        if (!res.ok) return false;
        try { sessionStorage.setItem('fd-applied', this.code); } catch (e) { /* ignore */ }
        this.showApplied();
        return true;
      } catch (e) {
        return false;
      }
    }

    async copy() {
      const applying = this.applyToCheckout();
      const ok = await copyText(this.code);
      clearTimeout(this._copyTimer);

      if (!ok) {
        if (this.live) this.live.textContent = 'Kód sa nepodarilo skopírovať. Zadaj ho ručne: ' + this.code;
        return;
      }

      // restart the ring animation when clicked again during the 2 s window
      if (this.card.hasAttribute('data-copied')) {
        this.card.removeAttribute('data-copied');
        void this.card.offsetWidth;
      }
      this.card.setAttribute('data-copied', '');
      if (this.live) this.live.textContent = 'Kód ' + this.code + ' je skopírovaný';
      applying.then((applied) => {
        if (applied && this.live) this.live.textContent = 'Kód ' + this.code + ' je skopírovaný a automaticky sa uplatní v pokladni';
      });

      this._copyTimer = setTimeout(() => {
        this.card.removeAttribute('data-copied');
        if (this.live) this.live.textContent = '';
      }, 2000);
    }
  }

  customElements.define('fall-deal', FallDeal);

  /* ---------- variant change listeners (event driven, no polling) ---------- */
  let frame = 0;
  let pendingId = null;
  function scheduleSync(variantId) {
    if (variantId) pendingId = String(variantId);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const id = pendingId;
      pendingId = null;
      instances.forEach((inst) => inst.sync(id && inst.variants.has(id) ? id : undefined));
    });
  }

  // Dawn sets input[name="id"] and dispatches a bubbling "change"; custom size pickers usually do too
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;
    if (t.name === 'id' || t.closest('variant-selects, variant-radios, product-info, .product-form__input, [data-fd-variant-source]')) {
      scheduleSync(t.name === 'id' ? t.value : null);
    }
  }, true);

  // custom pickers that only click and update the URL
  document.addEventListener('click', (e) => {
    if (instances.size && e.target && e.target.closest && e.target.closest('variant-selects, variant-radios, .product-form__input, [data-fd-variant-source]')) {
      scheduleSync();
    }
  }, true);

  // common theme / app events
  ['variant:change', 'variant:changed', 'variantChange'].forEach((name) => {
    document.addEventListener(name, (e) => {
      const v = e.detail && (e.detail.variant || e.detail);
      scheduleSync(v && v.id);
    });
  });

  // Dawn 10+ pub/sub
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const v = event && event.data && event.data.variant;
        scheduleSync(v && v.id);
      });
    }
  } catch (e) { /* not Dawn or older Dawn - change listener covers it */ }

  window.addEventListener('pageshow', () => scheduleSync());

  // exposed for tests only
  window.__fallDeal = { computeDeal, formatEur, codeSavings };
})();
